import apiClient from '../axios.js';

/**
 * 訂單批次匯入 API
 */
export const orderImportService = {
  /**
   * 上傳匯入檔案
   * @param {File} file - 上傳的檔案
   * @param {string} format - 檔案格式 (csv, excel, json)
   * @param {string} encoding - 編碼 (選填，預設 utf-8)
   * @returns {Promise<Object>} 上傳結果
   */
  uploadFile: async (file, format, encoding = 'utf-8') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('format', format);
    formData.append('encoding', encoding);

    const response = await apiClient.post('/orders/import/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 60000 // 60 秒超時（大檔案上傳需要更長時間）
    });
    return response;
  },

  /**
   * 設定欄位映射
   * @param {string} uploadId - 上傳 ID
   * @param {Object} fieldMapping - 欄位映射
   * @param {boolean} hasHeader - 是否包含標題列
   * @returns {Promise<Object>} 映射結果
   */
  setFieldMapping: async (uploadId, fieldMapping, hasHeader = true) => {
    const response = await apiClient.post('/orders/import/mapping', {
      uploadId,
      fieldMapping,
      hasHeader
    });
    return response;
  },

  /**
   * 預覽與驗證資料
   * @param {string} uploadId - 上傳 ID
   * @param {string} mappingId - 映射 ID
   * @param {number} previewRows - 預覽行數
   * @returns {Promise<Object>} 預覽結果
   */
  previewData: async (uploadId, mappingId, previewRows = 10) => {
    const response = await apiClient.post('/orders/import/preview', {
      uploadId,
      mappingId,
      previewRows
    });
    return response;
  },

  /**
   * 執行匯入
   * @param {string} uploadId - 上傳 ID
   * @param {string} mappingId - 映射 ID
   * @param {Object} options - 匯入選項
   * @returns {Promise<Object>} 匯入作業
   */
  executeImport: async (uploadId, mappingId, options = {}) => {
    const response = await apiClient.post('/orders/import/execute', {
      uploadId,
      mappingId,
      options
    });
    return response;
  },

  /**
   * 查詢匯入作業狀態
   * @param {string} jobId - 作業 ID
   * @returns {Promise<Object>} 作業狀態
   */
  getJobStatus: async (jobId) => {
    const response = await apiClient.get(`/orders/import/jobs/${jobId}/status`);
    return response;
  },

  /**
   * 查詢匯入作業結果
   * @param {string} jobId - 作業 ID
   * @returns {Promise<Object>} 作業結果
   */
  getJobResult: async (jobId) => {
    const response = await apiClient.get(`/orders/import/jobs/${jobId}/result`);
    return response;
  },

  /**
   * 查詢匯入作業建立的訂單明細
   * @param {string} jobId - 作業 ID
   * @param {Object} params - 分頁參數
   * @returns {Promise<Object>} 訂單明細列表
   */
  listJobRecords: async (jobId, params = {}) => {
    const response = await apiClient.get(`/orders/import/jobs/${jobId}/records`, { params });
    return response;
  },

  /**
   * 查詢匯入作業列表
   * @param {Object} params - 查詢參數
   * @returns {Promise<Object>} 作業列表
   */
  listJobs: async (params = {}) => {
    const response = await apiClient.get('/orders/import/jobs', { params });
    return response;
  },

  /**
   * 取得原始匯入檔案的預簽名下載連結
   * @param {string} jobId - 作業 ID
   * @returns {Promise<Object>} 下載連結資訊
   */
  getDownloadUrl: async (jobId) => {
    const response = await apiClient.get(`/orders/import/jobs/${jobId}/download`);
    return response;
  },

  /**
   * 整批撤銷匯入作業
   * @param {string} jobId - 作業 ID
   * @returns {Promise<Object>} 撤銷結果
   */
  revokeImportJob: async (jobId) => {
    const response = await apiClient.post(`/orders/import/jobs/${jobId}/revoke`);
    return response;
  }
};
